import { useCallback, useEffect, useRef, useState } from "react";
import {
  Search,
  Upload,
  Store,
  CheckCircle2,
  Circle,
  PackageSearch,
  AlertTriangle,
  Ban,
  RotateCcw,
  Trash2,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDT } from "@/lib/sound";
import { useImportJob } from "@/hooks/useImportJob";
import { ImportProgress } from "@/components/ImportProgress";

const inputCls = "w-full bg-white border border-[#D9E3DC] rounded-md px-3 py-2 text-sm text-[#0F2A1F] focus:outline-none focus:ring-2 focus:ring-[#0F6B3A] placeholder:text-[#8A9A90]";
const CLASSE = { ZERADO: ["Zerado", "bg-[#FEE2E2] text-[#B91C1C]"], BAIXO: ["Estoque mínimo", "bg-[#FFF4D6] text-[#9A6B0A]"], OK: ["OK", "bg-[#D1FAE5] text-[#047857]"] };

export default function Separacao() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [limite, setLimite] = useState(25);
  const [status, setStatus] = useState("todos");
  const [classe, setClasse] = useState("todas");
  const ref = useRef(null);

  const fetchAll = useCallback(async () => {
    try { setData((await api.get("/separacao", { params: { q: q || undefined, limite, status, classe } })).data); }
    catch { toast.error("Erro ao carregar SKUs alocados"); }
  }, [q, limite, status, classe]);
  useEffect(() => { const t = setTimeout(fetchAll, 250); return () => clearTimeout(t); }, [fetchAll]);

  const { job, start, running } = useImportJob((r) => { toast.success(`${r.skus} SKUs · ${r.unidades} un. importados (base substituída)`); fetchAll(); });

  const togglePego = async (it) => {
    try { await api.post(`/separacao/${it.codigo}/pego`, { pego: !it.pego }); fetchAll(); }
    catch { toast.error("Erro ao marcar item"); }
  };
  const limparPegos = async () => {
    if (!window.confirm("Desmarcar todos os itens coletados na loja?")) return;
    try { await api.post("/separacao/pegos/reset"); toast.success("Marcações limpas"); fetchAll(); } catch { toast.error("Erro ao limpar"); }
  };

  const limparImportacao = async () => {
    if (!window.confirm("Limpar a importação atual? Todos os itens alocados e as marcações de coletado serão removidos.")) return;
    try { const { data: r } = await api.post("/separacao/importacao/reset"); toast.success(`Importação limpa · ${r.removidos} SKUs removidos`); fetchAll(); } catch { toast.error("Erro ao limpar importação"); }
  };

  const exportar = async () => {
    try {
      const res = await api.get("/separacao/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "skus-alocados-er.csv";
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Itens alocados exportados");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao exportar itens");
    }
  };

  const k = data?.kpi;
  const kpis = k ? [
    ["SKUs alocados", k.skus, `${k.unidades} unidades em pedidos online`, PackageSearch, "#0F2A1F"],
    [`Verificar na loja (≤ ${limite})`, k.verificar_loja, "estoque físico até o limite", Store, "#B8860B"],
    ["Zerados", k.zerados, "disponível ≤ 0", Ban, "#B91C1C"],
    ["Coletados na loja", k.pegos, `${k.skus - k.pegos} pendentes`, CheckCircle2, "#047857"],
  ] : [];

  return (
    <div className="space-y-5" data-testid="separacao-page">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
        <div>
          <div className="text-[11px] font-mono tracking-[0.2em] text-[#5A6B62] uppercase">Estoque compartilhado</div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F2A1F]">SKUs Alocados ER</h1>
          <p className="text-sm text-[#5A6B62] mt-1">Itens alocados em pedidos online que precisam ser verificados/retirados da loja.</p>
        </div>
        <div className="flex items-center gap-2">
          <input ref={ref} type="file" accept=".xlsx,.csv" className="hidden" data-testid="separacao-import-input"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) start("/separacao/import", f); e.target.value = ""; }} />
          <button data-testid="separacao-import-btn" disabled={running} onClick={() => ref.current?.click()}
            className="bg-[#2DC26B] hover:bg-[#22A85B] disabled:opacity-60 text-white font-bold text-sm uppercase tracking-wide px-4 py-2.5 rounded-md flex items-center gap-2 shadow-sm">
            <Upload className="w-4 h-4" /> Importar itens empenhados ER
          </button>
          <button
            data-testid="separacao-export-btn"
            onClick={exportar}
            className="bg-white hover:bg-[#F1F5F2] text-[#0F6B3A] border border-[#0F6B3A]/40 font-semibold text-sm px-3 py-2.5 rounded-md flex items-center gap-2"
          >
            <Download className="w-4 h-4" /> Exportar itens
          </button>
          {k?.pegos > 0 && <button data-testid="separacao-limpar-pegos" onClick={limparPegos} className="bg-white hover:bg-[#F1F5F2] text-[#0F2A1F] border border-[#D9E3DC] font-medium text-sm px-3 py-2.5 rounded-md flex items-center gap-2"><RotateCcw className="w-4 h-4" /> Limpar coletados</button>}
          {k?.skus > 0 && <button data-testid="separacao-limpar-importacao" onClick={limparImportacao} className="bg-white hover:bg-[#FEF2F2] text-[#B91C1C] border border-[#FECACA] font-medium text-sm px-3 py-2.5 rounded-md flex items-center gap-2"><Trash2 className="w-4 h-4" /> Limpar importação</button>}
        </div>
      </div>
      {job && <div className="bg-white border border-[#D9E3DC] rounded-lg p-3"><ImportProgress job={job} testid="separacao-import-progress" /></div>}
      {data?.ultima && <div className="font-mono text-[11px] text-[#8A9A90] -mt-2" data-testid="separacao-ultima">Última importação: {data.ultima.arquivo} · {data.ultima.usuario} · {fmtDT(data.ultima.at)} · {data.ultima.skus} SKUs / {data.ultima.unidades} un.</div>}

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {kpis.map(([t, v, s, Icon, c], i) => (
          <div key={t} className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm p-4 flex items-start justify-between" data-testid={`sep-kpi-${i}`}>
            <div><p className="text-xs font-semibold text-[#5A6B62] uppercase tracking-wide">{t}</p><p className="text-3xl font-extrabold font-mono mt-1" style={{ color: c }}>{v}</p><p className="text-[11px] text-[#8A9A90]">{s}</p></div>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${c}18` }}><Icon className="w-5 h-5" style={{ color: c }} /></div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm p-3 grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="relative md:col-span-6">
          <Search className="w-4 h-4 text-[#8A9A90] absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="separacao-busca-input" value={q} onChange={(e) => setQ(e.target.value)} className={`${inputCls} pl-9`} placeholder="Buscar por código ou nome do produto... (vários: separe com ;)" />
        </div>
        <select data-testid="separacao-limite-select" value={limite} onChange={(e) => setLimite(Number(e.target.value))} className={`${inputCls} md:col-span-2`}>
          {[10, 15, 20, 25, 30, 50].map((n) => <option key={n} value={n}>Regra: estoque ≤ {n}</option>)}
        </select>
        <select data-testid="separacao-status-select" value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} md:col-span-2`}>
          <option value="todos">Todos</option><option value="verificar">Verificar na loja</option><option value="pendentes">Pendentes</option><option value="pegos">Coletados</option>
        </select>
        <select data-testid="separacao-classe-select" value={classe} onChange={(e) => setClasse(e.target.value)} className={`${inputCls} md:col-span-2`}>
          <option value="todas">Todas as classes</option><option value="ZERADO">Zerado</option><option value="BAIXO">Estoque mínimo</option><option value="OK">OK</option>
        </select>
      </div>

      <div className="space-y-3" data-testid="separacao-cards">
        {!data ? <div className="py-16 text-center text-[#8A9A90]">Carregando...</div>
          : data.itens.length === 0 ? (
            <div className="bg-white border border-[#D9E3DC] rounded-lg py-14 text-center" data-testid="separacao-empty">
              <div className="text-[#0F2A1F] font-semibold">{data.kpi.skus === 0 ? "Nenhuma importação feita." : "Nenhum item com estes filtros."}</div>
              <div className="text-xs text-[#8A9A90] mt-1">{data.kpi.skus === 0 ? 'Use o botão "IMPORTAR ITENS EMPENHADOS ER" com a relação dos pedidos online (código + quantidade).' : "Ajuste a busca ou a regra de estoque."}</div>
            </div>
          ) : data.itens.map((it) => (
            <div key={it.codigo} className={`bg-white border rounded-lg shadow-sm px-4 py-4 flex flex-col md:flex-row md:items-center justify-between gap-3 ${it.pego ? "opacity-60 border-[#A7F3D0]" : "border-[#D9E3DC]"}`} data-testid={`sep-card-${it.codigo}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-sm font-semibold text-[#0F6B3A]">{it.codigo}</span>
                  {it.verificar_loja && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#FFF4D6] text-[#9A6B0A] text-[10px] font-bold uppercase" data-testid={`sep-verificar-${it.codigo}`}><Store className="w-3 h-3" /> Verificar na loja</span>}
                  {it.classe === "ZERADO" && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#FEE2E2] text-[#B91C1C] text-[10px] font-bold uppercase"><AlertTriangle className="w-3 h-3" /> {CLASSE.ZERADO[0]}</span>}
                </div>
                <div className="font-semibold text-[#0F2A1F] truncate mt-0.5" title={it.nome}>{it.nome}</div>
                <div className="font-mono text-xs text-[#5A6B62] mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                  <span data-testid={`sep-alocado-${it.codigo}`}>Alocado: <b className="text-[#2DC26B]">{it.qtd_alocada}</b></span>
                  <span>Disponível: <b className={it.disponivel <= 0 ? "text-[#B91C1C]" : "text-[#047857]"}>{it.disponivel}</b></span>
                  <span title={`Alocado no sistema: ${it.alocado_sistema}`}>Saldo: <b className={it.fisica <= 0 ? "text-[#B91C1C]" : it.verificar_loja ? "text-[#B8860B]" : "text-[#0F2A1F]"}>{it.fisica}</b></span>
                  {it.retirar > 0 && <span data-testid={`sep-retirar-${it.codigo}`} className="text-[#B91C1C] font-bold uppercase">· Retirar: {it.retirar}</span>}
                </div>
                <div className="font-mono text-[11px] text-[#5A6B62] mt-1 flex flex-wrap gap-x-2">
                  {it.localizacoes.length ? it.localizacoes.slice(0, 4).map((l) => <span key={l.endereco} className={l.tipo === "pulmao" ? "text-[#8A9A90]" : "text-[#0F2A1F] font-semibold"}>{l.endereco}{l.tipo === "pulmao" ? " (P)" : ""}</span>) : <span className="text-[#8A9A90]">Sem endereço</span>}
                </div>
                {it.situacoes?.length ? <div className="text-[10px] text-[#8A9A90] mt-0.5 truncate">{it.situacoes.map((s) => `${s.situacao} (${s.qtd})`).join(" · ")}</div> : null}
                {it.pego && <div className="text-[10px] text-[#8A9A90] mt-0.5 font-mono">Coletado por {it.pego_por} · {fmtDT(it.pego_em)}</div>}
              </div>
              <div className="shrink-0">
                <button data-testid={`sep-ok-${it.codigo}`} onClick={() => togglePego(it)} title={it.pego ? `Coletado por ${it.pego_por} · ${fmtDT(it.pego_em)}` : "Marcar como coletado na loja"}
                  className={`h-12 w-full md:w-auto md:min-w-[110px] px-6 text-base font-bold tracking-wider rounded-md border-2 flex items-center justify-center gap-2 transition-colors ${it.pego ? "bg-[#10B981] border-[#10B981] text-white" : "bg-white border-[#A7F3D0] text-[#047857] hover:bg-[#ECFDF5]"}`}>
                  {it.pego ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5" />} OK
                </button>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
